import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { Package, MessageCircle, Check, Clock, Truck } from "lucide-react";

const STATUS_MAP = {
  pending_payment: { label: "Esperando pago", color: "text-white/60", icon: Clock },
  preparing: { label: "Preparando tu pedido", color: "text-arre-red", icon: Package },
  shipped: { label: "En camino", color: "text-arre-red", icon: Truck },
  delivered: { label: "Entregado", color: "text-green-500", icon: Check },
  paid: { label: "Pagado - Preparando", color: "text-arre-red", icon: Package },
};

export default function OrderTrackingPage() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const search = async (e) => {
    e.preventDefault();
    setErr("");
    setOrder(null);
    if (!orderNumber || !email) {
      setErr("Ingresa número de orden y correo.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/track?order_number=${orderNumber.trim()}&email=${email.trim()}`);
      setOrder(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "No encontramos ese pedido.");
    } finally {
      setLoading(false);
    }
  };

  const statusKey = order?.payment_status === "paid" ? (order?.order_status || "preparing") : "pending_payment";
  const statusInfo = STATUS_MAP[statusKey] || STATUS_MAP.pending_payment;
  const StatusIcon = statusInfo.icon;

  const waMsg = order
    ? encodeURIComponent(`Hola compare, consulto mi pedido ${order.order_number} de Arre Sonora.`)
    : "";
  const whatsappUrl = `https://wa.me/526621234567?text=${waMsg}`;

  return (
    <div className="bg-arre-black min-h-screen py-16" data-testid="tracking-page">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Rastreo</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-none mb-3">
          ¿Dónde anda<br /><span className="text-arre-red">mi pedido?</span>
        </h1>
        <p className="font-serif-display italic text-xl text-white/70 mb-10">
          Ingresa tu número de orden y correo para ver el estado.
        </p>

        <form onSubmit={search} className="space-y-4 bg-arre-surface border border-arre-border p-6">
          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-white/60">Número de orden</label>
            <input
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
              placeholder="AS-12345678"
              className="w-full mt-1 bg-arre-black border border-arre-border px-4 py-3 text-white font-display tracking-wider focus:outline-none focus:border-arre-red"
              data-testid="track-order-input"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-white/60">Correo usado en el pedido</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 bg-arre-black border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red"
              data-testid="track-email-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-arre w-full bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase py-4 disabled:opacity-50"
            data-testid="track-submit"
          >
            {loading ? "Buscando..." : "Rastrear pedido"}
          </button>
          {err && <p className="text-arre-red text-sm" data-testid="track-error">{err}</p>}
        </form>

        {order && (
          <div className="mt-10 bg-arre-surface border border-arre-border p-8" data-testid="track-result">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-arre-red/15 border border-arre-red/40 flex items-center justify-center">
                <StatusIcon className={`w-6 h-6 ${statusInfo.color}`} strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-xs tracking-widest uppercase text-white/50">Estado</div>
                <div className={`font-display text-3xl tracking-wider uppercase ${statusInfo.color}`}>
                  {statusInfo.label}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-arre-border">
              <div>
                <div className="text-xs tracking-widest uppercase text-white/50">Orden</div>
                <div className="font-display text-2xl text-white">{order.order_number}</div>
              </div>
              <div>
                <div className="text-xs tracking-widest uppercase text-white/50">Total</div>
                <div className="font-display text-2xl text-white">{fmtMXN(order.total)}</div>
              </div>
            </div>

            <h3 className="font-display text-xl tracking-wider uppercase text-white mb-3">Productos</h3>
            <div className="space-y-2 mb-6">
              {order.items.map((it, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm border-b border-arre-border/50 py-2">
                  <span className="text-white/80">{it.name} x{it.quantity}</span>
                  <span className="text-white/60">{fmtMXN(it.line_total)}</span>
                </div>
              ))}
            </div>

            <div className="text-sm text-white/70">
              <div><span className="text-white/50">Dirección:</span> {order.shipping?.street}, {order.shipping?.city}, {order.shipping?.state} CP {order.shipping?.zip}</div>
              <div className="mt-1"><span className="text-white/50">Contacto:</span> {order.shipping?.phone}</div>
            </div>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-6 btn-arre bg-green-600 hover:bg-green-700 text-white font-display text-lg tracking-widest uppercase py-3 px-6 inline-flex items-center gap-2"
              data-testid="track-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              Consultar por WhatsApp
            </a>
          </div>
        )}

        <div className="mt-10 text-center">
          <Link to="/" className="text-white/60 hover:text-white text-sm tracking-widest uppercase">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}
